import {Card, DataStatus, Label} from 'platform/components';
import {Center, Grid, Heading, VStack} from 'platform/foundation';
import {useDateTimeFormatter, useFormatCurrency} from 'platform/locale';

import {useCallback} from 'react';

import {defaultTo, isNotEmpty, isNotNil, mergeAll} from 'ramda';

import {TenantResponseBody} from '@dms/api/shared';
import {useGetArticleStockItemOverviewQuery} from '@dms/api/warehouse';
import i18n from '@dms/i18n';
import {DmsDataGrid, Section, WAREHOUSE_GRID_CODES} from '@dms/shared';
import {getQuantityWithHandlingUnit} from '@dms/warehouse/shared';
import {useWarehouseParams} from '@dms/warehouse/shared-components';

import {parseDate} from 'shared/web';

import type {EagGridMaximization, QueryFilterObject} from 'features/eag-grid';

interface ReceiptsProps {
  maximization?: EagGridMaximization;
  tenant: TenantResponseBody;
  handlingUnitLabel: string;
}

export function Receipts(props: ReceiptsProps) {
  const params = useWarehouseParams();
  const currencyFormatter = useFormatCurrency();
  const dateFormatter = useDateTimeFormatter();

  const {
    data: articleStockItemOverview,
    isLoading: isArticleStockItemOverviewLoading,
    isError: isArticleStockItemError,
  } = useGetArticleStockItemOverviewQuery(
    {
      warehouseId: params.warehouseId,
      articleId: params.articleId,
    },
    {
      refetchOnMountOrArgChange: true,
    }
  );

  const isLoading = isArticleStockItemOverviewLoading;
  const isError = isArticleStockItemError;

  const queryModifier = useCallback(
    (filter: QueryFilterObject) => mergeAll([filter, {articleId: params.articleId}]),
    [params]
  );

  if (!articleStockItemOverview) {
    return (
      <Center width="100%">
        <DataStatus isLoading={isLoading} isError={isError} minHeight="100vh" />
      </Center>
    );
  }

  const totalValueCurrency = currencyFormatter(
    defaultTo(0, Number(articleStockItemOverview.totalValue)),
    props.tenant.currency,
    2
  );
  const lastReceptionDate =
    isNotNil(articleStockItemOverview.lastReception) &&
    isNotEmpty(articleStockItemOverview.lastReception)
      ? dateFormatter('dateTimeShort', parseDate(articleStockItemOverview.lastReception))
      : '-';
  const oldestPurchaseDate =
    isNotNil(articleStockItemOverview.oldestPurchaseDate) &&
    isNotEmpty(articleStockItemOverview.oldestPurchaseDate)
      ? dateFormatter('dateShort', parseDate(articleStockItemOverview.oldestPurchaseDate))
      : '-';
  return (
    <Section isFullHeight>
      <VStack spacing={4} height="100%">
        <Card title={i18n.t('entity.warehouse.labels.receiptsOverview')}>
          <Grid columns={4}>
            <VStack>
              <Label>{i18n.t('entity.warehouse.labels.totalInventory')}</Label>
              <Heading size={2}>
                {getQuantityWithHandlingUnit(
                  articleStockItemOverview.totalInventory,
                  props.handlingUnitLabel
                )}
              </Heading>
            </VStack>
            <VStack>
              <Label>{i18n.t('entity.warehouse.labels.totalValue')}</Label>
              <Heading size={2}>{totalValueCurrency}</Heading>
            </VStack>
            <VStack>
              <Label>{i18n.t('entity.warehouse.labels.lastReception')}</Label>
              <Heading size={2}>{lastReceptionDate}</Heading>
            </VStack>
            <VStack>
              <Label>{i18n.t('entity.warehouse.labels.oldestPurchaseInStockDate')}</Label>
              <Heading size={2}>{oldestPurchaseDate}</Heading>
            </VStack>
          </Grid>
        </Card>
        <Card title={i18n.t('entity.warehouse.labels.receipts')} isFullHeight>
          <DmsDataGrid
            maximization={props.maximization}
            gridCode={WAREHOUSE_GRID_CODES.articleReceipts}
            data-testid={WAREHOUSE_GRID_CODES.articleReceipts}
            queryModifier={queryModifier}
          />
        </Card>
      </VStack>
    </Section>
  );
}
